import {
  AssetLevelOfSupport,
  InterfaceOrganizationComponent,
} from '@manifest-cyber/types/interface/dbTables';
import { Box, Flex, Stack, Text, Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  MRT_ColumnDef,
  MRT_GlobalFilterTextInput,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from 'mantine-react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MappedOrganizationComponentWithVulns } from '../../api/componentsApi/components.mapper';
import { useUpdateComponent } from '../../api/componentsApi/useUdpateComponent';
import { useFetchUserHasPermissions } from '../../api/user/useFetchUserHasPermissions';
import ClickableRegion from '../../components/ClickableRegion';
import {
  ColumnConfig,
  ColumnsConfigPopover,
} from '../../components/ColumnsConfigPopover/ColumnsConfigPopover';
import { ComponentsLicenseCell } from '../../components/DataTables/ComponentsLicenseCell';
import DataTableFooter from '../../components/DataTables/DataTableFooter';
import { EditableRow } from '../../components/DataTables/EditableRow/EditableRow';
import { HeaderWithTooltip } from '../../components/DataTables/HeaderWithTooltip/HeaderWithTooltip';
import Icon from '../../components/Icon/Icon';
import { getDefaultTableOptions } from '../../components/MRT/ManifestMRT';
import SupplierModal from '../../components/SupplierModal';
import { useComponentsFilter } from '../../hooks/components/useComponentsFilter';
import {
  COMPONENTS_REACT_QUERY_KEY,
  useFetchComponents,
} from '../../hooks/queries/useFetchComponents';
import { ManifestNotifications } from '../../hooks/utils/useNotifications';
import { useOrganizationId } from '../../hooks/utils/useOrganizationId';
import { useURLandLocalStorageSortingState } from '../../hooks/utils/usePersistentStates';
import normalizeAuthor from '../../lib/normalizeAuthor';
import { captureExceptionWithMessage } from '../../lib/sentry/captureExceptionWithMessage/captureExceptionWithMessage';
import './ComponentsTable.scss';
import { EndOfSupportModal } from './ComponentsTable/EndOfSupportModal/EndOfSupportModal';
import { LastReleaseModal } from './ComponentsTable/LastReleaseModal/LastReleaseModal';
import { LevelOfSupportModal } from './ComponentsTable/LevelOfSupportModal/LevelOfSupportModal';
import {
  ComponentsColumnId,
  getDefaultComponentsColumnsConfig,
} from './api/pageConfig/componentsPageConfig.mapper';
import { useGetComponentsPageConfig } from './api/pageConfig/useGetComponentsPageConfig';
import { useUpdateComponentsPageConfig } from './api/pageConfig/useUpdateComponentsPageConfig';
import mergeLicensesAndLicensesData from '../../lib/mergeLicensesAndLicensesData';

export const NOASSERTION = 'NOASSERTION';

export const renderText = (text: string = '') => {
  return (
    <Tooltip disabled={(text?.length ?? 0) <= 30} label={text}>
      <Box className="cell-content-container">
        <Text lineClamp={2} className="truncated-text">
          {text}
        </Text>
      </Box>
    </Tooltip>
  );
};

const defaultTableOptions =
  getDefaultTableOptions<MappedOrganizationComponentWithVulns>();

interface Props {
  assetId?: string;
}

export const ComponentsTable = ({ assetId }: Props) => {
  const { t } = useTranslation();
  const hasWriteAccess = useFetchUserHasPermissions({
    requiredPermissions: ['update:component'],
  });
  const [currentOrgId] = useOrganizationId();
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showSupportLevelModal, setShowSupportLevelModal] = useState(false);
  const [showLastReleaseModal, setShowLastReleaseModal] = useState(false);
  const [showEndOfSupportModal, setShowEndOfSupportModal] = useState(false);
  const [componentToEdit, setComponentToEdit] =
    useState<MappedOrganizationComponentWithVulns | null>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: updateComponent, isLoading: isUpdatingComponent } =
    useUpdateComponent();
  const { getCurrentFilters, componentName, setComponentName } = useComponentsFilter();
  const { data: pageConfig } = useGetComponentsPageConfig({
    t,
    options: { placeholderData: { columnsConfig: getDefaultComponentsColumnsConfig(t) } },
  });

  const { mutateAsync: updateComponentsPageConfig } = useUpdateComponentsPageConfig();

  // Ensure all configured frontend columns are available in the column config
  const currentColumnsConfig = useMemo(() => {
    const backendConfig = pageConfig?.columnsConfig || [];
    const defaultConfig = getDefaultComponentsColumnsConfig(t);

    // Create a mapping of default column positions for reference
    const defaultColumnPositions = defaultConfig.reduce(
      (positions, col, index) => {
        positions[col.id] = index;
        return positions;
      },
      {} as Record<string, number>,
    );

    // Check if any columns from default config are missing in backend config
    const missingColumns = defaultConfig.filter(
      (defaultCol) =>
        !backendConfig.some((backendCol) => backendCol.id === defaultCol.id),
    );

    // If no missing columns, just use the backend config
    if (missingColumns.length === 0) {
      return backendConfig;
    }

    // If there are missing columns, we need to insert them at their proper positions
    const mergedConfig = [...backendConfig];

    // Add missing columns at their appropriate positions
    missingColumns.forEach((missingCol) => {
      const defaultPosition = defaultColumnPositions[missingCol.id] ?? 0;

      // Find where to insert the column
      let insertIndex = 0;

      // Look for the column that should come before this one
      for (let i = defaultPosition - 1; i >= 0; i--) {
        const prevDefaultCol = defaultConfig[i];
        if (prevDefaultCol) {
          const prevColId = prevDefaultCol.id;
          const prevColIndex = mergedConfig.findIndex((col) => col.id === prevColId);

          if (prevColIndex !== -1) {
            insertIndex = prevColIndex + 1;
            break;
          }
        }
      }

      // Insert the missing column at the appropriate position
      mergedConfig.splice(insertIndex, 0, missingCol);
    });

    return mergedConfig;
  }, [pageConfig?.columnsConfig]);

  const newColumns = useMemo<MRT_ColumnDef<MappedOrganizationComponentWithVulns>[]>(
    () => [
      {
        accessorFn: (row) => row.lastReleaseDate,
        id: 'lastReleaseDate',
        header: t('tables.components.headers.lastReleaseDate'),
        minSize: 250,
        Header: ({ column }) => (
          <HeaderWithTooltip
            text={column.columnDef.header}
            tooltip={t('tables.components.headers.lastReleaseDateTooltip')}
          />
        ),
        Cell: ({ row }) => {
          return (
            <EditableRow
              isEditable={hasWriteAccess}
              useMutedText={!row.original.lastReleaseDateText}
              onClick={() => {
                setComponentToEdit(row.original);
                setShowLastReleaseModal(true);
              }}
            >
              {renderText(row.original.lastReleaseDateText || '--')}
            </EditableRow>
          );
        },
      },
      {
        accessorFn: (row) => row.levelOfSupport,
        id: 'levelOfSupport',
        header: t('tables.components.headers.levelOfSupport'),
        Cell: ({ row }) => {
          return (
            <EditableRow
              isEditable={hasWriteAccess}
              useMutedText={!row.original.levelOfSupportText}
              onClick={() => {
                setComponentToEdit(row.original);
                setShowSupportLevelModal(true);
              }}
            >
              {renderText(row.original.levelOfSupportText || '--')}
            </EditableRow>
          );
        },
      },
      {
        accessorFn: (row) => row.endOfSupport,
        id: 'endOfSupport',
        header: t('tables.components.headers.endOfSupport'),
        Cell: ({ row }) => {
          return (
            <EditableRow
              isEditable={hasWriteAccess}
              useMutedText={!row.original.endOfSupportText}
              onClick={() => {
                setComponentToEdit(row.original);
                setShowEndOfSupportModal(true);
              }}
            >
              {renderText(row.original.endOfSupportText || '--')}
            </EditableRow>
          );
        },
      },
    ],
    [],
  );

  const columns = useMemo<MRT_ColumnDef<MappedOrganizationComponentWithVulns>[]>(() => {
    const allColumns: MRT_ColumnDef<MappedOrganizationComponentWithVulns>[] = [
      {
        accessorFn: (row) => row.name,
        id: 'name',
        header: t('tables.components.headers.name'),
        minSize: 200,
        Cell: ({ row }) => {
          if (row.original?._id) {
            const displayName = row.original.nameToDisplay;

            return (
              <Tooltip
                label={row.original?.fullyQualifiedName}
                multiline
                disabled={!row.original?.fullyQualifiedName}
              >
                <ClickableRegion
                  className="table-link fully-qualified-name"
                  regionLabel={row.original?.fullyQualifiedName}
                  href={`/component/${row.original?._id}/${currentOrgId || ''}`}
                >
                  {displayName}
                </ClickableRegion>
              </Tooltip>
            );
          }

          return renderText(row.original?.name);
        },
      },
      {
        accessorFn: (row) => row.fullyQualifiedName,
        id: 'fullyQualifiedName',
        header: t('tables.components.headers.longName'),
        /*
         * Don't remove the line below (sortDescFirst), it is used to sort the column in descending order by default.
         * Contact marcelo@manitescyber.com if needed
         */
        sortDescFirst: false,
        Cell: ({ cell }) => renderText(cell.getValue<string>()),
      },
      {
        accessorFn: (row) =>
          row.packageUrl ||
          row.cpes?.join(', ') ||
          row.packageUrlNoVersion ||
          t('global.notProvided'),
        id: 'packageUrl',
        header: t('tables.components.headers.componentIdentifier'),
        Cell: ({ row, cell }) => {
          const value = cell.getValue<string>();
          if (value && value !== t('global.notProvided')) {
            const hasTooltipContent =
              row.original.packageUrl ||
              (row.original.cpes && row.original.cpes.length > 0);
            return (
              <Tooltip
                disabled={!hasTooltipContent}
                label={
                  <Flex direction="column" align="flex-start">
                    {row.original.packageUrl ? (
                      <Text>
                        <b className="uppercase">{t('global.purl')}:</b>{' '}
                        {row.original.packageUrl}
                      </Text>
                    ) : null}
                    {row.original.cpes?.length ? (
                      <Text align="left">
                        <b className="uppercase">{t('global.cpe')}:</b>{' '}
                        {row.original.cpes?.map((cpe, index) => (
                          <Text key={index}>{cpe}</Text>
                        ))}
                      </Text>
                    ) : null}
                  </Flex>
                }
              >
                <Box className="cell-content-container">
                  <Text lineClamp={2} className="truncated-text">
                    {value}
                  </Text>
                </Box>
              </Tooltip>
            );
          } else {
            return <span className="table-na">{t('global.notProvided')}</span>;
          }
        },
      },
      {
        accessorFn: (row) => row.version || '',
        id: 'version',
        header: t('tables.components.headers.version'),
        Cell: ({ row }) => {
          const version =
            row.original.version === NOASSERTION ? '--' : row.original.version;
          return renderText(version || '');
        },
      },
      {
        accessorFn: (row) => renderText(row.derivedEcosystem || ''),
        id: 'derivedEcosystem',
        header: t('tables.components.headers.ecosystem'),
      },
      {
        accessorFn: (row) => row.vulnerabilities,
        id: 'countVulnerabilities.total',
        header: t('tables.components.headers.vulns'),
        Cell: ({ row }) => {
          const vulns = row.original.vulnerabilities || [];
          const cveIds = vulns.map((v) => v.cveId);
          if (cveIds?.length) {
            if (cveIds.length > 1) {
              return (
                <Tooltip
                  multiline
                  width={200}
                  disabled={!cveIds?.length}
                  label={
                    <Stack>
                      {cveIds.map((cveId) => (
                        <Text key={cveId}>{cveId}</Text>
                      ))}
                    </Stack>
                  }
                >
                  <Box className="cell-content-container">
                    <Text lineClamp={2} className="truncated-text">
                      {`${cveIds.slice(0, 1).join(', ')}... `}
                      <Text component="span" color="cyan">
                        +{cveIds.length - 1}
                      </Text>
                    </Text>
                  </Box>
                </Tooltip>
              );
            }
            return renderText(cveIds.join(', '));
          }
          return <span className="table-na">{t('global.noneFound')}</span>;
        },
      },
      {
        id: 'licensesData.fullName',
        header: t('tables.components.headers.license'),
        accessorFn: (row) => {
          return mergeLicensesAndLicensesData(row.licenses ?? [], row.licensesData ?? []);
        },
        Cell: ComponentsLicenseCell,
        enableSorting:
          false /* temporary while we fix api error when sorting components by license: https://linear.app/manifest-cyber/issue/MFST-4390 */,
      },
      {
        enableSorting: false,
        accessorFn: (row) => row.supplier,
        id: 'supplier.name',
        header: t('tables.components.headers.source'),
        Header: ({ column }) => (
          <HeaderWithTooltip
            text={column.columnDef.header}
            tooltip={t('tables.components.headers.source-tooltip')}
          />
        ),
        Cell: ({ row }) => {
          const normalizedSource = normalizeAuthor(row.original, t, 'author');

          const normalizedAuthor =
            normalizedSource === 'unknown-source'
              ? t('jargon.unknown-source')
              : normalizedSource;

          return (
            <>
              <span
                className={`${normalizedSource === 'unknown-source' ? 'table-na' : ''}`}
              >
                {renderText(normalizedAuthor)}
                {hasWriteAccess && (
                  <ClickableRegion
                    regionLabel="Modify source information."
                    onClick={() => {
                      setComponentToEdit(row.original);
                      setShowSupplierModal(true);
                    }}
                    className="inline-list-link"
                  >
                    <>
                      <Icon icon="pencil" />
                    </>
                  </ClickableRegion>
                )}
              </span>
            </>
          );
        },
      },
      {
        accessorFn: (row) => row.description,
        id: 'description',
        header: t('tables.components.headers.description'),
        Cell: ({ cell }) => {
          const cellValue = cell.getValue<string>();
          if (cellValue?.length) {
            return renderText(cellValue);
          }
          return <span className="table-na">{t('global.notProvided')}</span>;
        },
      },
      {
        accessorFn: (row) => row.dateModified,
        id: 'dateModified',
        header: t('tables.components.headers.lastUpdated'),
        Header: ({ column }) => (
          <HeaderWithTooltip
            text={column.columnDef.header}
            tooltip={t('tables.components.headers.lastUpdatedTooltip')}
          />
        ),
        Cell: ({ row }) => renderText(row.original.dateModifiedText),
      },
      ...newColumns,
    ];

    return allColumns.filter((column) => {
      const columnConfig = currentColumnsConfig.find((c) => c.id === column.id);
      return columnConfig?.visible;
    });
  }, [currentColumnsConfig]);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSortingState] = useURLandLocalStorageSortingState('components', []);

  const {
    data: {
      data: fetchedComponents = [],
      queryInfo: { totalCount: countComponents = 0 } = {},
    } = {},
    isFetching: isFetchingComponents,
    isLoading: isLoadingComponents,
    isError: isErrorLoadingComponents,
  } = useFetchComponents({
    queryParams: {
      assetId,
      sort: sorting[0],
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      filters: getCurrentFilters(),
    },
    translations: {
      activelyMaintained: t('tables.components.supportLevel.activelyMaintained'),
      noMaintained: t('tables.components.supportLevel.noMaintained'),
      abandoned: t('tables.components.supportLevel.abandoned'),
      unknown: t('global.unknown'),
    },
  });

  const getOrderedColumnIds = useMemo(() => {
    // Use this function to get column IDs in the proper order based on default config
    const defaultConfig = getDefaultComponentsColumnsConfig(t);
    const defaultColumnOrder = defaultConfig.map((col) => col.id);

    return (columnConfigs: typeof currentColumnsConfig) => {
      // Map current columns to their default positions
      const columnPositionMap = new Map<string, number>();
      columnConfigs.forEach((col) => {
        const defaultPosition = defaultColumnOrder.indexOf(col.id);
        columnPositionMap.set(col.id, defaultPosition !== -1 ? defaultPosition : 999);
      });

      // Sort column IDs based on their default positions
      return [...columnConfigs]
        .sort((a, b) => {
          const posA = columnPositionMap.get(a.id) || 999;
          const posB = columnPositionMap.get(b.id) || 999;
          return posA - posB;
        })
        .map((col) => col.id);
    };
  }, []);

  const table = useMantineReactTable<MappedOrganizationComponentWithVulns>({
    ...defaultTableOptions,
    mantinePaperProps: {
      className: 'manifest-data-table-no-footer',
    },
    columns,
    data: fetchedComponents,
    enableFilters: false,
    enablePagination: false, // Disable pagination for this table
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: countComponents,
    initialState: {
      ...defaultTableOptions.initialState,
      showGlobalFilter: true,
      columnPinning: {
        left: [...currentColumnsConfig.filter((col) => col.locked).map((col) => col.id)],
      },
      columnOrder: getOrderedColumnIds(currentColumnsConfig),
    },
    state: {
      globalFilter: componentName,
      pagination,
      sorting,
      isLoading: isLoadingComponents,
      showLoadingOverlay: isFetchingComponents,
      columnPinning: {
        left: [...currentColumnsConfig.filter((col) => col.locked).map((col) => col.id)],
      },
      columnOrder: getOrderedColumnIds(currentColumnsConfig),
    },
    onPaginationChange: setPagination,
    onSortingChange: setSortingState,
    onGlobalFilterChange: setComponentName,
    mantineSearchTextInputProps: {
      placeholder: t('tables.components.search-components'),
      sx: {
        minWidth: '260px',
      },
    },
    renderTopToolbar: ({ table }) => (
      <Flex justify="space-between" m="1rem 0">
        <Flex gap="xs">
          <MRT_GlobalFilterTextInput table={table} />
          <div>
            <ColumnsConfigPopover
              // Filter out duplicate columns before sorting them
              columns={(() => {
                // Get the column order based on default config
                const defaultConfig = getDefaultComponentsColumnsConfig(t);
                const defaultColumnOrder = defaultConfig.map((col) => col.id);

                // Create a map to track columns by ID to avoid duplicates
                const uniqueColumns = new Map();

                // Add each column to the map, using its ID as key
                currentColumnsConfig.forEach((col) => {
                  // Make sure we're only storing one column per ID
                  if (!uniqueColumns.has(col.id)) {
                    uniqueColumns.set(col.id, col);
                  }
                });

                // Convert back to array and sort by default order
                return Array.from(uniqueColumns.values()).sort((a, b) => {
                  const posA = defaultColumnOrder.indexOf(a.id);
                  const posB = defaultColumnOrder.indexOf(b.id);
                  // If column isn't in default config, put it at the end
                  return (posA === -1 ? 999 : posA) - (posB === -1 ? 999 : posB);
                });
              })()}
              onChange={async (columConfig) => {
                try {
                  await updateComponentsPageConfig({
                    columnsConfig: columConfig as ColumnConfig<ComponentsColumnId>[],
                  });
                } catch (error) {
                  ManifestNotifications.showErrorNotification({
                    title: t('global.unexpectedError'),
                    message: t('global.unexpectedErrorSubtitle'),
                  });
                  captureExceptionWithMessage('Error updateComponentsPageConfig', error);
                }
              }}
              title={t('vulnerabilities.table.controls.customizeColumns')}
            />
          </div>
        </Flex>
      </Flex>
    ),
  });

  const updateComponentInfo = async ({
    levelOfSupport,
    endOfSupport,
    lastReleaseDate,
    updatedFieldName,
  }: {
    levelOfSupport?: AssetLevelOfSupport;
    endOfSupport?: string;
    lastReleaseDate?: string;
    updatedFieldName: string;
  }) => {
    if (!componentToEdit?._id) {
      return;
    }

    try {
      await updateComponent({
        id: componentToEdit._id,
        body: {
          levelOfSupport,
          endOfSupport,
          lastReleaseDate,
        },
      });

      ManifestNotifications.showSuccessNotification({
        title: t('tables.components.savedNewValue', { field: updatedFieldName }),
        message: `${t('tables.components.forComponent')}: ${componentToEdit.nameToDisplay}`,
      });
    } catch (error) {
      ManifestNotifications.showErrorNotification({
        title: t('global.unexpectedError'),
        message: t('global.unexpectedErrorSubtitle'),
      });
    }
  };

  return (
    <>
      {isErrorLoadingComponents && (
        <ul className="page-errors anim-slideInUpShort">
          <li>{t('tables.components.unable-to-fetch-components')}</li>
        </ul>
      )}
      {componentToEdit && (
        <SupplierModal
          opened={showSupplierModal}
          onCancel={() => {
            setComponentToEdit(null);
            setShowSupplierModal(false);
          }}
          onConfirm={() => {
            queryClient.invalidateQueries([COMPONENTS_REACT_QUERY_KEY]);
            setComponentToEdit(null);
            setShowSupplierModal(false);
          }}
          component={componentToEdit as InterfaceOrganizationComponent}
        />
      )}
      <div className="list-components">
        <MantineReactTable table={table} />
        <DataTableFooter
          currentPage={pagination.pageIndex}
          limitPerPage={pagination.pageSize}
          totalResults={countComponents}
          onChangePage={setPagination}
        />
        {showSupportLevelModal && componentToEdit && (
          <LevelOfSupportModal
            initialValue={componentToEdit.levelOfSupport as AssetLevelOfSupport}
            componentName={componentToEdit.nameToDisplay}
            showLoadingButton={isUpdatingComponent}
            onUpdate={async (levelOfSupport) => {
              await updateComponentInfo({
                levelOfSupport,
                updatedFieldName: t('tables.components.headers.levelOfSupport'),
              });
              setComponentToEdit(null);
              setShowSupportLevelModal(false);
            }}
            onClose={() => {
              setComponentToEdit(null);
              setShowSupportLevelModal(false);
            }}
          />
        )}
        {showEndOfSupportModal && componentToEdit && (
          <EndOfSupportModal
            initialValue={componentToEdit.endOfSupport}
            componentName={componentToEdit.nameToDisplay}
            showLoadingButton={isUpdatingComponent}
            onUpdate={async (endOfSupport) => {
              await updateComponentInfo({
                endOfSupport,
                updatedFieldName: t('tables.components.headers.endOfSupport'),
              });
              setComponentToEdit(null);
              setShowEndOfSupportModal(false);
            }}
            onClose={() => {
              setComponentToEdit(null);
              setShowEndOfSupportModal(false);
            }}
          />
        )}
        {showLastReleaseModal && componentToEdit && (
          <LastReleaseModal
            initialValue={componentToEdit.lastReleaseDate}
            componentName={componentToEdit.nameToDisplay}
            showLoadingButton={isUpdatingComponent}
            onUpdate={async (lastReleaseDate) => {
              await updateComponentInfo({
                lastReleaseDate,
                updatedFieldName: t('tables.components.headers.lastReleaseDate'),
              });
              setComponentToEdit(null);
              setShowLastReleaseModal(false);
            }}
            onClose={() => {
              setComponentToEdit(null);
              setShowLastReleaseModal(false);
            }}
          />
        )}
      </div>
    </>
  );
};
